import type { ColorSpec } from './types';

/**
 * Black color spec — uses the `coal` Tailwind palette.
 *
 * Modeled on the blue spec: very dark filled bg (#1f1f1f) with white text.
 * Soft and outline variants follow the standard light/dark pattern.
 */
export const black: ColorSpec = {
  filled: {
    border: 'border-coal-700 dark:border-coal-500',
    bg: 'bg-coal-600 text-white dark:bg-coal-700',
    bgActive: 'bg-coal-800 text-white',
    hover: 'hover:bg-coal-700 hover:text-white active:bg-coal-800 active:text-white',
    hoverDark: 'dark:hover:bg-coal-800 dark:hover:text-white dark:active:bg-coal-900 dark:active:text-white',
  },
  outline: {
    border: 'border-coal-400 dark:border-coal-500',
    bg: 'bg-coal-50 dark:bg-coal-950 text-coal-700 dark:text-coal-200',
    bgActive: 'bg-coal-600 dark:bg-coal-500 text-white',
    hover: 'hover:bg-coal-100 hover:text-coal-800 active:bg-coal-200 active:text-coal-800',
    hoverDark: 'dark:hover:bg-coal-900 dark:hover:text-coal-100 dark:active:bg-coal-800 dark:active:text-coal-100',
    hoverWhenActive: 'hover:bg-coal-700 dark:hover:bg-coal-600',
  },
  soft: {
    border: 'border-coal-100 dark:border-coal-900',
    bg: 'bg-coal-50 dark:bg-coal-950 text-coal-700 dark:text-coal-300 font-normal',
    bgActive: 'bg-coal-600 dark:bg-coal-500 text-white',
    hover: 'hover:bg-coal-600 hover:text-white active:bg-coal-700 active:text-white',
    hoverDark: 'dark:hover:bg-coal-500 dark:hover:text-white dark:active:bg-coal-600 dark:active:text-white',
    hoverWhenActive: 'hover:bg-coal-700 dark:hover:bg-coal-600',
  },
  textFilled: {
    text: 'text-coal-600 dark:text-coal-400',
    activeBg: 'bg-coal-600 text-white dark:bg-coal-700',
    hover: 'hover:bg-coal-600 hover:text-white active:bg-coal-700',
    hoverDark: 'dark:hover:bg-coal-700 dark:hover:text-white dark:active:bg-coal-800',
  },
  textSoft: {
    text: 'text-coal-700 dark:text-coal-300 font-normal',
    activeBg: 'bg-coal-600 text-white dark:bg-coal-500',
    hover: 'hover:bg-coal-100 hover:text-coal-900 active:bg-coal-200',
    hoverDark: 'dark:hover:bg-coal-900 dark:hover:text-coal-100 dark:active:bg-coal-800',
  },
  tooltip: 'bg-coal-600 dark:bg-coal-700 text-white',
  badge: {
    filled: 'bg-coal-600 dark:bg-coal-700 text-white',
    soft: 'bg-coal-400 dark:bg-coal-500 text-white',
  },
  softIcon: 'text-coal-700 dark:text-coal-200 group-hover/btn:text-current',
  underline: {
    filled: '[&_u]:text-coal-200 dark:[&_u]:text-coal-300 [&_u]:decoration-coal-300 dark:[&_u]:decoration-coal-400',
    outline: '[&_u]:text-coal-800 dark:[&_u]:text-coal-300 [&_u]:decoration-coal-600 dark:[&_u]:decoration-coal-400',
    soft: '[&_u]:text-coal-800 dark:[&_u]:text-coal-300 [&_u]:decoration-coal-300 dark:[&_u]:decoration-coal-400',
  },
  input: {
    filled: {
      border: 'border-coal-700 dark:border-coal-500',
      bg: 'bg-coal-600 dark:bg-coal-700',
      focusBg: 'focus-within:bg-coal-700 dark:focus-within:bg-coal-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-coal-700 dark:focus-within:outline-coal-500',
      hoverActive:
        'hover:bg-coal-700 hover:text-white active:bg-coal-800 active:text-white dark:hover:bg-coal-800 dark:active:bg-coal-900',
      readOnlyBg: 'bg-coal-500 dark:bg-coal-800',
      text: 'text-white',
    },
    outline: {
      border: 'border-coal-400 dark:border-coal-500',
      bg: 'bg-coal-50 dark:bg-coal-950',
      focusBg: 'focus-within:bg-coal-100 dark:focus-within:bg-coal-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-coal-400 dark:focus-within:outline-coal-500',
      hoverActive:
        'hover:bg-coal-100 hover:text-coal-800 active:bg-coal-200 active:text-coal-800 dark:hover:bg-coal-900 dark:hover:text-coal-100 dark:active:bg-coal-800 dark:active:text-coal-100',
      readOnlyBg: 'bg-coal-50 dark:bg-coal-900',
      text: 'text-coal-700 dark:text-coal-200',
    },
    soft: {
      border: 'border-coal-100 dark:border-coal-900',
      bg: 'bg-coal-50 dark:bg-coal-950',
      focusBg: 'focus-within:bg-coal-100 dark:focus-within:bg-coal-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-coal-100 dark:focus-within:outline-coal-900',
      hoverActive:
        'hover:bg-coal-100 hover:text-coal-900 active:bg-coal-200 active:text-coal-950 dark:hover:bg-coal-900 dark:hover:text-coal-100 dark:active:bg-coal-800 dark:active:text-coal-100',
      readOnlyBg: 'bg-coal-50 dark:bg-coal-900',
      text: 'text-coal-700 dark:text-coal-300',
    },
  },
  inputMuted: {
    filled: 'text-white',
    outline: 'text-coal-300 dark:text-coal-600',
    soft: 'text-coal-300 dark:text-coal-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-white',
    outline: 'placeholder:text-coal-300 dark:placeholder:text-coal-600',
    soft: 'placeholder:text-coal-300 dark:placeholder:text-coal-600',
  },
  group: {
    filled: 'border-coal-300 dark:border-coal-700 bg-coal-50 dark:bg-coal-950',
    outline: 'border-coal-400 dark:border-coal-500 bg-coal-50 dark:bg-coal-950',
    soft: 'border-coal-200 dark:border-coal-800 bg-coal-100 dark:bg-coal-900',
  },
  groupDivider: {
    filled: 'bg-coal-300 dark:bg-coal-700',
    outline: 'bg-coal-300 dark:bg-coal-600',
    soft: 'bg-coal-200 dark:bg-coal-700',
  },
  segment: {
    filled: 'border-coal-300 dark:border-coal-700 bg-coal-50 dark:bg-coal-950',
    outline: 'border-coal-400 dark:border-coal-500 bg-coal-50 dark:bg-coal-950',
  },
  segmentDivider: {
    filled: 'bg-coal-300 dark:bg-coal-700',
    outline: 'bg-coal-300 dark:bg-coal-600',
  },
  popoverHighlight: 'bg-coal-600 text-white',
  popoverText: 'text-coal-700 dark:text-coal-200',
  popoverBorder: 'border-coal-200 dark:border-coal-700',
};
